import { useEffect, useState } from "react"
import { ZIndex } from "constants/zIndex"
import { onBootSplashRequested, switchedAccount } from "bootMode"
import { useAuthStore } from "stores"
import { hostName, testHostName } from "utils/constants"
import "./logo-loader.css"

/** One pass of the logo animation, matching logo-loader.css. */
const CYCLE_MS = 2600

const TAGLINE = ""

/**
 * The words in the brand yellow, by position: the second "work" and "Africa" -- the verb and
 * who it is for. Highlighting both "work"s made the line read as a stutter.
 */
const HIGHLIGHTED_WORDS = [2, 4]

/** The verb, leaning: "making work *work*" is the turn the line hangs on. */
const ITALIC_WORDS = [2]

/** How long the finished logo holds before the screen fades. */
const FADE_MS = 260

const LOGO_PATHS = (
  <>
    <path d="M138.444 0.262248C59.9827 -4.6853 -4.68518 59.9825 0.26237 138.444C4.35328 203.147 56.8533 255.647 121.556 259.738C200.018 264.703 264.685 200.018 259.738 121.556C255.647 56.8532 203.147 4.35316 138.444 0.262248ZM139.021 240.769C70.5421 246.171 13.8288 189.441 19.2309 120.962C23.4792 67.0979 67.133 23.4441 120.997 19.1958C189.476 13.7937 246.189 70.507 240.787 138.986C236.539 192.85 192.902 236.504 139.021 240.752V240.769Z" />
    <path
      className="logo-loader__check logo-loader__check--top"
      d="M99.4583 94.6503H81.8185C80.752 94.6503 79.7206 95.0699 78.9688 95.8216L68.6716 106.119C67.0982 107.692 64.5457 107.692 62.9898 106.119L57.8674 100.996C56.294 99.423 53.7415 99.423 52.1856 100.996L43.3744 109.808C41.801 111.381 41.801 113.934 43.3744 115.489L63.0248 135.14C64.5982 136.713 67.1506 136.713 68.7066 135.14L80.3674 123.479L102.325 101.503C104.86 98.9685 103.06 94.6328 99.4758 94.6328L99.4583 94.6503Z"
    />
    <path
      className="logo-loader__check logo-loader__check--middle"
      d="M157.395 94.6503H139.756C138.689 94.6503 137.658 95.0699 136.906 95.8216L97.6577 135.07C96.0842 136.643 93.5318 136.643 91.9758 135.07L86.801 129.895C85.2276 128.322 82.6751 128.322 81.1192 129.895L72.308 138.706C70.7346 140.28 70.7346 142.832 72.308 144.388L91.9758 164.056C93.5493 165.629 96.1017 165.629 97.6577 164.056L109.301 152.413H109.318L160.263 101.503C162.798 98.9685 160.997 94.6328 157.413 94.6328L157.395 94.6503Z"
    />
    <path
      className="logo-loader__check logo-loader__check--bottom"
      d="M215.507 94.6503H197.867C196.801 94.6503 195.769 95.0699 195.018 95.8216L126.731 164.108C125.157 165.682 122.605 165.682 121.049 164.108L115.874 158.934C114.301 157.36 111.748 157.36 110.192 158.934L101.381 167.745C99.8078 169.318 99.8078 171.871 101.381 173.427L121.049 193.094C122.622 194.668 125.175 194.668 126.731 193.094L138.392 181.434H138.409L218.357 101.503C220.892 98.9685 219.091 94.6328 215.507 94.6328V94.6503Z"
    />
  </>
)

/**
 * The logo drawing itself in: the ring holds still while the three checks arrive top to
 * bottom. It takes its colour from `color`, so it works on white or on the brand blue.
 */
export const LogoLoader = ({
  size = 72,
  className = "",
}: {
  size?: number
  className?: string
}) => (
  <svg
    className={`logo-loader text-primary500 ${className}`}
    width={size}
    height={size}
    viewBox="0 0 260 260"
    role="img"
    aria-label="Loading PaidHR"
  >
    {LOGO_PATHS}
  </svg>
)

const isOwnDomain = () => {
  const hostname = window.location.hostname
  return (
    hostname === hostName ||
    hostname === testHostName ||
    hostname === "localhost"
  )
}

/**
 * The logo, once, when the app first opens.
 *
 * Deliberately one pass and no more. A looping loader says "still waiting", which is not what
 * this is: the app is starting, it takes about as long as the animation, and the animation
 * ends on the finished logo rather than on an empty ring. Once it has played the app carries
 * on exactly as before — the boot skeleton, the page placeholders and the spinners are all
 * untouched, and switching account still draws the app shell in grey rather than this.
 *
 * Only on our own domains: a white-label customer must not be shown our logo while their own
 * branding is still loading.
 */
/**
 * The line under the mark.
 *
 * It fades up once the third check has landed, so the logo finishes drawing before anything
 * asks to be read and the two do not compete.
 */
const Tagline = () => (
  <span className="logo-loader-tagline font-gilroy text-xl text-primary500">
    {TAGLINE?.split(" ").map((word, wordIndex, words) => (
      <span
        key={wordIndex}
        className={[
          HIGHLIGHTED_WORDS.includes(wordIndex) ? "logo-loader-highlight" : "",
          ITALIC_WORDS.includes(wordIndex) ? "italic" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {word}
        {wordIndex < words.length - 1 ? " " : ""}
      </span>
    ))}
  </span>
)

/**
 * When the logo plays: on a fresh load for someone already signed in, and the moment someone
 * signs in. Not for a signed-out visitor on their way to the login page -- the mark announcing
 * an app they have not entered yet, then a login form, reads as a false start.
 */
export const BootSplash = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<"playing" | "leaving" | "done">(() =>
    isOwnDomain() && !switchedAccount && useAuthStore.getState().isAuthenticated
      ? "playing"
      : "done"
  )

  // Counts each play, so a second one remounts the logo and its animation starts from the
  // top rather than resuming a finished one.
  const [run, setRun] = useState(0)

  useEffect(
    () =>
      onBootSplashRequested(() => {
        if (!isOwnDomain()) return
        setRun((count) => count + 1)
        setState("playing")
      }),
    []
  )

  useEffect(() => {
    if (state !== "playing") return

    const timer = window.setTimeout(() => setState("leaving"), CYCLE_MS)
    return () => window.clearTimeout(timer)
  }, [state, run])

  useEffect(() => {
    if (state !== "leaving") return

    const timer = window.setTimeout(() => setState("done"), FADE_MS)
    return () => window.clearTimeout(timer)
  }, [state])

  return (
    <>
      {/* Mounted underneath from the first frame: the app gets on with starting while the
          logo plays, so the animation costs nothing beyond itself. */}
      {children}

      {state === "done" ? null : (
        <div
          className="logo-loader-screen"
          data-leaving={state === "leaving"}
          style={{ zIndex: ZIndex.LoadingOverlay }}
          role="status"
          aria-label="Loading PaidHR"
        >
          <div
            key={run}
            className="flex flex-col items-center gap-5"
          >
            <LogoLoader size={72} />
            <Tagline />
          </div>
        </div>
      )}
    </>
  )
}

export default LogoLoader
